/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import skillsPageParameters from './__generated__/SkillsPageQuery$parameters';
import type { SkillsPageQuery } from './__generated__/SkillsPageQuery.graphql.ts';

// The entrypoint preloads the first page of categories; follow-up category pages
// are loaded via usePaginationFragment on agentWorkspace_skillCategoriesInProject
// inside useSpaceSkillsConnection. Keep this small so the initial payload is fast
// and the rest stream in via outer infinite scroll (CORCAP-6705).
const SPACE_SKILLS_CATEGORIES_FIRST = 20;

// Prefetch the first page of skills inline with the initial query so the
// (pre-expanded) first category renders its skills with no extra client
// round-trip. `limitSkillsToFirstCategories: 1` scopes the inline `projectSkills`
// to just the first category to keep the initial payload small; other categories
// load their skills lazily on first expand via useSpaceSkillsConnection.
const SPACE_SKILLS_PER_CATEGORY_FIRST = 20;
const SPACE_SKILLS_LIMIT_TO_FIRST_CATEGORIES = 1;
const SPACE_SKILLS_AGENT_INITIAL_FETCHING = true;

type SkillsEntryPointComponent = EntryPointComponent<
	{
		skillsPageQuery: SkillsPageQuery;
	},
	{},
	{},
	{
		cloudId: string;
		projectKey: string;
	}
>;

export const skillsEntryPoint: EntryPoint<SkillsEntryPointComponent, EntryPointRouteParams> =
	createEntryPoint({
		root: JSResourceForInteraction<SkillsEntryPointComponent>(
			/* webpackChunkName: "async-servicedesk-skills", jiraSpaEntry: "async-servicedesk-skills" */ './SkillsPage.tsx',
		),
		getPreloadProps: ({
			context: { match },
			tenantContext: { cloudId },
		}: EntryPointRouteParams) => {
			const projectKey = match.params.projectKey ?? '';

			return {
				queries: {
					skillsPageQuery: {
						options: {
							fetchPolicy: 'network-only' as const,
						},
						parameters: skillsPageParameters,
						variables: {
							cloudId,
							projectKey,
							categoriesFirst: SPACE_SKILLS_CATEGORIES_FIRST,
							skillsLimit: SPACE_SKILLS_PER_CATEGORY_FIRST,
							limitSkillsToFirstCategories: SPACE_SKILLS_LIMIT_TO_FIRST_CATEGORIES,
							agentInitialFetching: SPACE_SKILLS_AGENT_INITIAL_FETCHING,
						},
					},
				},
				extraProps: {
					cloudId,
					projectKey,
				},
			};
		},
	});
